import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent, type DragEvent } from 'react';
import { toast } from 'sonner';
import { uploadFiles } from '@/utils/api-handler';
import type { AttachedFileMetadata } from '@/types/command-agent';

/** Local attachment row: staged file + its upload lifecycle. `uploaded` is set once the server has accepted it. */
export interface AttachmentItem {
    localId: string;
    file: File;
    status: 'pending' | 'uploading' | 'done' | 'error';
    uploaded?: AttachedFileMetadata;
}

const COMMAND_TEAM_FILES_UPLOAD_ENDPOINT = '/api/command-team/files';

export const ACCEPT_FILES =
    '.txt,.pdf,.doc,.docx,.md,.csv,.html,.htm,.screen,.xlsx,.xls,text/html,text/markdown,image/*,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';
export const MAX_ATTACH_FILES = 5;
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

function parseCommandTeamFileUploadResponse(res: unknown): AttachedFileMetadata | undefined {
    if (!res || typeof res !== 'object') return undefined;
    const coerce = (o: unknown): AttachedFileMetadata | undefined => {
        if (!o || typeof o !== 'object') return undefined;
        const x = o as Record<string, unknown>;
        const id = x.id ?? x.file_id;
        const name =
            typeof x.file_name === 'string' ? x.file_name : typeof x.fileName === 'string' ? (x.fileName as string) : undefined;
        if (id == null || name == null) return undefined;
        return {
            id: String(id),
            file_name: name,
            file_url: typeof x.file_url === 'string' ? x.file_url : typeof x.fileUrl === 'string' ? (x.fileUrl as string) : undefined,
            file_size: typeof x.file_size === 'number' ? x.file_size : typeof x.fileSize === 'number' ? (x.fileSize as number) : undefined,
            mime_type: typeof x.mime_type === 'string' ? x.mime_type : typeof x.mimeType === 'string' ? (x.mimeType as string) : undefined,
        };
    };
    const root = res as Record<string, unknown>;
    return (
        coerce(res) ??
        coerce(root.file) ??
        coerce(Array.isArray(root.files) ? root.files[0] : undefined) ??
        coerce(root.data)
    );
}

/**
 * Owns the staged attachment queue for a chat input: drag/drop/paste/select → pending,
 * an auto-upload effect drives pending → uploading → done (or drops the row on failure).
 * Returns stable handlers and a `clear()` for callers to drain after sending a message.
 */
export function useFileAttachmentQueue() {
    const [attachedFiles, setAttachedFiles] = useState<AttachmentItem[]>([]);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    /** Prevents double-uploading the same localId (React Strict Mode, effect re-entry). Pruned when rows disappear. */
    const uploadKickoffRef = useRef<Set<string>>(new Set());

    const processFiles = useCallback((files: File[]) => {
        if (!files.length) return;

        const overSize: File[] = [];
        const validFiles: File[] = [];
        files.forEach((file) => {
            if (file.size > MAX_FILE_SIZE_BYTES) overSize.push(file);
            else validFiles.push(file);
        });
        if (overSize.length > 0) {
            toast.error(
                overSize.length === 1
                    ? `"${overSize[0].name}" is over 100 MB and was not added.`
                    : `${overSize.length} file(s) over 100 MB were not added.`,
            );
        }

        setAttachedFiles((prev) => {
            const newItems = validFiles
                .slice(0, MAX_ATTACH_FILES - prev.length)
                .map((file) => ({
                    localId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    file,
                    status: 'pending' as const,
                }));
            if (newItems.length === 0) return prev;
            return [...prev, ...newItems].slice(0, MAX_ATTACH_FILES);
        });
    }, []);

    useEffect(() => {
        const activeIds = new Set(attachedFiles.map((a) => a.localId));
        for (const id of [...uploadKickoffRef.current]) {
            if (!activeIds.has(id)) uploadKickoffRef.current.delete(id);
        }

        const toStart = attachedFiles.filter((item) => item.status === 'pending' && !uploadKickoffRef.current.has(item.localId));
        if (toStart.length === 0) return;

        for (const item of toStart) uploadKickoffRef.current.add(item.localId);

        const startIds = new Set(toStart.map((i) => i.localId));
        setAttachedFiles((prev) => prev.map((a) => (startIds.has(a.localId) ? { ...a, status: 'uploading' as const } : a)));

        for (const { localId, file } of toStart) {
            uploadFiles<Record<string, unknown>>(COMMAND_TEAM_FILES_UPLOAD_ENDPOINT, [file], { fileKey: 'file' })
                .then((res) => {
                    const meta = parseCommandTeamFileUploadResponse(res);
                    if (meta?.id) {
                        setAttachedFiles((prev) =>
                            prev.map((a) => (a.localId === localId ? { ...a, status: 'done' as const, uploaded: meta } : a)),
                        );
                    } else {
                        setAttachedFiles((prev) => prev.filter((a) => a.localId !== localId));
                        toast.error(`Failed to upload ${file.name}`);
                    }
                })
                .catch(() => {
                    setAttachedFiles((prev) => prev.filter((a) => a.localId !== localId));
                    toast.error(`Failed to upload ${file.name}`);
                })
                .finally(() => {
                    uploadKickoffRef.current.delete(localId);
                });
        }
    }, [attachedFiles]);

    const handleFileSelect = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files?.length) return;
        processFiles(Array.from(files));
        e.target.value = '';
    }, [processFiles]);

    const handleDragOver = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types.includes('Files')) e.dataTransfer.dropEffect = 'copy';
        setIsDraggingOver(true);
    }, []);

    const handleDragLeave = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDraggingOver(false);
    }, []);

    const handleDrop = useCallback((e: DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingOver(false);
        const files = e.dataTransfer.files;
        if (!files?.length) return;
        processFiles(Array.from(files));
    }, [processFiles]);

    const handlePaste = useCallback((e: ClipboardEvent) => {
        const files = e.clipboardData?.files;
        if (!files?.length) return;
        // Pastes from docs (Word, Google Docs, Notion) carry both a rendered image
        // and the underlying text — prefer the text and let the default paste run.
        const types = e.clipboardData?.types;
        const hasText =
            (types?.includes('text/plain') && e.clipboardData?.getData('text/plain')) ||
            (types?.includes('text/html') && e.clipboardData?.getData('text/html'));
        if (hasText) return;
        e.preventDefault();
        processFiles(Array.from(files));
    }, [processFiles]);

    const removeAttachedFile = useCallback((index: number) => {
        setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const clear = useCallback(() => setAttachedFiles([]), []);

    return {
        attachedFiles,
        isDraggingOver,
        fileInputRef,
        processFiles,
        handleFileSelect,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handlePaste,
        removeAttachedFile,
        clear,
    };
}
